import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { Menu } from "lucide-react"
import { Logo } from "@/components/logo"
import { NotificationBell } from "@/components/notification-bell"
import { DeskUserMenu } from "@/components/desk-user-menu"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"

const NAV_LINKS: {
  to: "/recruit/jobs" | "/recruit/talent" | "/recruit/messages" | "/recruit/hackathons"
  label: string
  soon?: boolean
}[] = [
  { to: "/recruit/jobs", label: "Jobs" },
  { to: "/recruit/talent", label: "Find Talent", soon: true },
  { to: "/recruit/messages", label: "Messages" },
  { to: "/recruit/hackathons", label: "Hackathons" },
]

const SoonBadge = () => (
  <span className="ml-1.5 inline-flex items-center rounded-full bg-[color:var(--wf-lime-wash)] px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase leading-none tracking-wide text-[color:var(--wf-lime-text)]">
    Soon
  </span>
)

const activeLinkProps = {
  className: "mkt-nav-link mkt-nav-link--active",
}

/** Ink & lime chrome for the logged-in employer desk (/recruit/*) only. */
export function EmployerHeader({
  canManageHackathons = false,
  workspaceAvailable = false,
}: {
  canManageHackathons?: boolean
  workspaceAvailable?: boolean
}) {
  const [open, setOpen] = useState(false)
  const navLinks = workspaceAvailable
    ? NAV_LINKS.filter(
        (item) => item.to !== "/recruit/hackathons" || canManageHackathons,
      )
    : []

  return (
    <header className="mkt-nav mkt-nav--app home-wf">
      <div className="home-wf-shell mkt-nav-inner">
        <Link to="/recruit" className="home-wf-brand" aria-label="Hire employer desk">
          <Logo />
        </Link>

        <nav className="mkt-nav-links" aria-label="Primary">
          {navLinks.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="mkt-nav-link"
              activeProps={activeLinkProps}
            >
              {item.label}
              {item.soon && <SoonBadge />}
            </Link>
          ))}
        </nav>

        <div className="mkt-nav-actions mkt-nav-actions--desktop">
          <span className="mkt-nav-bell">
            <NotificationBell viewAllPath="/recruit/notifications" />
          </span>
          <DeskUserMenu />
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button type="button" className="mkt-nav-menu" aria-label="Open menu">
              <Menu className="size-5" aria-hidden />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="mkt-nav-sheet home-wf w-[min(100%,20rem)]">
            <SheetHeader>
              <SheetTitle className="sr-only">Menu</SheetTitle>
            </SheetHeader>
            <nav className="mkt-nav-drawer" aria-label="Mobile primary">
              {navLinks.map((item) => (
                <SheetClose asChild key={item.to}>
                  <Link
                    to={item.to}
                    className="mkt-nav-link"
                    activeProps={activeLinkProps}
                  >
                    {item.label}
                    {item.soon && <SoonBadge />}
                  </Link>
                </SheetClose>
              ))}
              <div className="mkt-nav-drawer-actions">
                <span className="mkt-nav-bell">
                  <NotificationBell viewAllPath="/recruit/notifications" />
                </span>
                <DeskUserMenu variant="drawer" onNavigate={() => setOpen(false)} />
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}
